import type { Graph } from '@/schema/estate';
import { FLAG_NAMES, flagOf } from './flags';
import { itemsByEstate } from './itemService';

export type TagFilter = { name: string; label: string; flag: boolean; itemIds: Set<string> };

const flagRank = (label: string) => {
  const i = (FLAG_NAMES as readonly string[]).indexOf(label);
  return i < 0 ? FLAG_NAMES.length : i;
};

/**
 * The tags on the estate's live items: flags in `FLAG_NAMES` order, then plain tags by name.
 * Keyed by name, because two devices tagging offline can each create a tag row of that name.
 */
export function tagFilters(g: Graph, estateId: string): TagFilter[] {
  const live = new Set(itemsByEstate(g, estateId).map((i) => i.id));
  const nameOf = new Map(
    g.tag
      .all()
      .filter((t) => t.estate_id === estateId && !t.archived)
      .map((t) => [t.id, t.name]),
  );
  const byName = new Map<string, TagFilter>();
  for (const row of g.item_tag.all()) {
    const name = row.tag_id ? nameOf.get(row.tag_id) : undefined;
    if (row.archived || !name || !row.item_id || !live.has(row.item_id)) continue;
    let filter = byName.get(name);
    if (!filter) {
      const flag = flagOf(name);
      filter = { name, label: flag ?? name, flag: flag !== undefined, itemIds: new Set() };
      byName.set(name, filter);
    }
    filter.itemIds.add(row.item_id);
  }
  return [...byName.values()].sort(
    (a, b) =>
      Number(b.flag) - Number(a.flag) ||
      (a.flag ? flagRank(a.label) - flagRank(b.label) : 0) ||
      a.label.localeCompare(b.label),
  );
}
